// #60 Dialog and #61 SlideOver: the two modal surfaces (glass over the scrim, DC-8). Each is role="dialog",
// aria-modal, titled by its h2, portalled into the nearest layer (inline under SSR and before mount, ground rule 9),
// with useFocusTrap: focus moves in (the body's first control, else the footer's, else the close button), Tab cycles
// inside, Escape closes, focus returns to the opener. The footer carries one red primary (DC-23), a ghost dismiss and
// an optional hint; a primary blocked until valid is dashed with its reason (DC-24), a busy one keeps its label under
// aria-busy; refused is the red banner at the top of the body, entered values kept. `preview` (a gallery section's
// static rendering): drawn in place, non-modal, no trap, the scrim confined to its box. With a footer dismiss the
// close button stays for the pointer but leaves the tab order and the accessibility tree (one tabbable close per
// surface), and the surface's hs moves to the dismiss, so a frame never stamps a hidden control.

import { useId, useRef, type ReactNode, type RefObject } from "react";
import { Avatar } from "../core/Person";
import { Banner } from "../core/Banner";
import { Button, IconButton, type Blocked } from "../core/Button";
import { cx } from "../lib/cx";
import { McPortal, usePortalTarget } from "../lib/McRoot";
import { useFocusTrap, focusables } from "../lib/useFocusTrap";
import { useIsoLayoutEffect } from "../lib/useIsoLayoutEffect";
import { vars } from "../lib/vars";
import { DIALOG_LABELS, OVERLAY_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

/** Why the surface asks to close: Escape, the close button, or the footer's dismiss. */
export type OverlayCloseReason = "escape" | "close" | "dismiss";

/** The footer's one primary action (DC-23: red, one per surface). */
export interface OverlayPrimary {
  label: string;
  onClick?: (() => void) | undefined;
  /** Blocked until valid: dashed, aria-disabled, his rule sentence as the description. */
  blocked?: Blocked | undefined;
  /** In flight: label kept, aria-busy, inert. */
  busy?: boolean | undefined;
  hs?: Hs | undefined;
}

/** The refused state: the server said no after the act. His rule sentence; `newer` for a stale version. */
export interface OverlayRefused {
  reason: string;
  newer?: ReactNode | undefined;
}

export interface OverlayProps {
  /** The title (an h2): his verb or noun, from a labels module. A Dialog may take a preset instead. */
  title?: string | undefined;
  children?: ReactNode;
  /** Default true: an overlay is rendered when it is open. */
  open?: boolean | undefined;
  onClose?: ((reason: OverlayCloseReason) => void) | undefined;
  primary?: OverlayPrimary | undefined;
  /** The ghost dismiss button's words (absent: no dismiss button; the close button and Escape still close). */
  dismissLabel?: string | undefined;
  /** Further footer actions, left of the dismiss and the primary. */
  actions?: ReactNode | undefined;
  /** A footer hint (an audit sentence), on the left. */
  hint?: ReactNode | undefined;
  refused?: OverlayRefused | undefined;
  /** The close button's name (aria-label and tooltip); default *Close* (pending, Q-3), never the dismiss's *Cancel*. */
  closeLabel?: string | undefined;
  /** The element that takes focus on opening, instead of the first control. */
  initialFocus?: RefObject<HTMLElement | null> | undefined;
  /** A gallery section's static rendering: in place, non-modal, inert, no trap. */
  preview?: boolean | undefined;
  /** false: the seat may not do this; nothing renders. */
  can?: boolean | undefined;
  /** Capture hotspot of the close: the footer dismiss when there is one, else the close button. */
  hs?: Hs | undefined;
  className?: string | undefined;
}

type Kind = "dialog" | "slide";

interface FrameProps extends OverlayProps {
  kind: Kind;
  width?: number | undefined;
  person?: { name: string; hueKey?: string | undefined; candidate?: boolean | undefined } | undefined;
}

function OverlayFrame(props: FrameProps) {
  const {
    kind, title, children, open = true, onClose, primary, dismissLabel, actions, hint, refused, closeLabel = OVERLAY_LABELS.close,
    initialFocus, preview = false, can, hs, className, width, person,
  } = props;
  const titleId = `${useId()}-title`;
  const surface = useRef<HTMLDivElement | null>(null);
  const body = useRef<HTMLDivElement | null>(null);
  const foot = useRef<HTMLDivElement | null>(null);
  const first = useRef<HTMLElement | null>(null);
  // Before the layer exists (the first render) the content is drawn in place and moves into the layer on the next
  // render. useFocusTrap follows the surface element across that move, and the first control is found again in the
  // layer's copy.
  const layer = usePortalTarget();
  const live = open && !preview && can !== false;

  // The control that takes focus on opening: the caller's, else the body's first, else the footer's first.
  useIsoLayoutEffect(() => {
    if (!live) return;
    first.current = initialFocus?.current
      ?? (body.current ? focusables(body.current)[0] : undefined)
      ?? (foot.current ? focusables(foot.current)[0] : undefined)
      ?? null;
  }, [live, initialFocus, layer]);
  useFocusTrap(surface, { active: live, preview, onEscape: () => onClose?.("escape"), initialFocus: first });

  if (can === false || !open) return null;
  const Tag = kind === "dialog" ? "div" : "aside";
  const box = (
    <Tag
      ref={surface as RefObject<HTMLDivElement>}
      role="dialog"
      aria-modal={preview ? undefined : true}
      aria-labelledby={titleId}
      tabIndex={-1}
      className={cx(kind === "dialog" ? "mc-dlg" : "mc-slide", className)}
      style={kind === "dialog" && width !== undefined ? vars({ "--mc-dlg-w": Math.min(840, Math.max(460, width)) }) : undefined}
    >
      <div className="mc-dlg-h">
        {person ? <Avatar name={person.name} hueKey={person.hueKey} candidate={person.candidate} size={36} decorative /> : null}
        <h2 id={titleId}>{title}</h2>
        <IconButton
          icon="x" label={closeLabel} sm className="mc-dlg-x" onClick={() => onClose?.("close")}
          {...(dismissLabel ? { tabIndex: -1, "aria-hidden": true } : { hs })}
        />
      </div>
      <div className="mc-dlg-b" ref={body}>
        {refused ? (
          <Banner tone="red" live className="mc-dlg-refused">
            {refused.reason}
            {refused.newer ? <div className="mc-dlg-newer">{refused.newer}</div> : null}
          </Banner>
        ) : null}
        {children}
      </div>
      {primary || dismissLabel || actions || hint ? (
        <div className="mc-dlg-f" ref={foot}>
          {hint ? <p className="mc-dlg-hint">{hint}</p> : null}
          {actions ?? null}
          {dismissLabel ? <Button tone="ghost" onClick={() => onClose?.("dismiss")} hs={hs}>{dismissLabel}</Button> : null}
          {primary ? (
            <Button tone="primary" blocked={primary.blocked} busy={primary.busy} hs={primary.hs} onClick={primary.onClick}>{primary.label}</Button>
          ) : null}
        </div>
      ) : null}
    </Tag>
  );
  if (preview) {
    return <div className={cx("mc-scrim", "mc-scrim-pv", kind === "slide" && "mc-scrim-slide")}>{box}</div>;
  }
  return (
    <McPortal>
      <div className={cx("mc-scrim", kind === "slide" && "mc-scrim-slide")}>{box}</div>
    </McPortal>
  );
}

/** The presets DS #60 lists: a preset is a title from DIALOG_LABELS. */
export type DialogPreset = Exclude<keyof typeof DIALOG_LABELS, "keepThisInterview" | "editProfile">;

export const DIALOG_PRESETS: readonly DialogPreset[] = [
  "schedule", "reschedule", "cancel", "declineThisInterview", "releasePackets", "merge", "recover", "designate", "linkToRequisition",
  "addCandidate", "requestHoldCancellationReopen", "recordDecline", "reviseOwnScore", "override",
];

export interface DialogProps extends OverlayProps {
  /** A DS #60 preset: its title (an explicit title wins). */
  preset?: DialogPreset | undefined;
  /** Width in px, 460–840 (default 540). */
  width?: number | undefined;
}

/** #60: the glass dialog over the scrim. */
export function Dialog({ preset, title, ...rest }: DialogProps) {
  return <OverlayFrame {...rest} kind="dialog" title={title ?? (preset ? DIALOG_LABELS[preset] : undefined)} />;
}

export interface SlideOverProps extends OverlayProps {
  /** The person or candidate the surface is about: a 36 px avatar before the title. */
  person?: { name: string; hueKey?: string | undefined; candidate?: boolean | undefined } | undefined;
}

/** #61: the 500 px glass slide-over from the right (full width ≤ 768), with the dialog's focus rules. */
export function SlideOver(props: SlideOverProps) {
  return <OverlayFrame {...props} kind="slide" />;
}
